<script setup lang="ts">
import { computed, markRaw, toRaw, useSlots, type Component } from 'vue';
import { badgeStyle } from './badgeRecipe';

export type BadgeLabelVariant =
  | 'base'
  | 'unavailable'
  | 'neutral'
  | 'positive'
  | 'negative'
  /** Pantry's "Attention Subtle". */
  | 'attention'
  | 'attentionBold'
  | 'accent';
export type BadgeLabelSize = 'small' | 'medium';

interface Props {
  variant?: BadgeLabelVariant;
  size?: BadgeLabelSize;
  iconLeading?: Component;
  liveStatus?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'base',
  size: 'medium',
  liveStatus: false,
  disabled: false,
});

const slots = useSlots();

/** Pantry sizes badge icons at 12px for both badge sizes. */
const ICON_SIZE = 12;

const iconLeadingForRender = computed(() => {
  if (props.iconLeading) return markRaw(toRaw(props.iconLeading));
});

const rootAttrs = computed(() => {
  const attrs: Record<string, string | boolean | undefined> = {};
  if (props.liveStatus && !props.disabled) {
    attrs.role = 'status';
  }
  if (props.disabled) {
    attrs['aria-hidden'] = 'true';
  }
  return attrs;
});
</script>

<template>
  <span :class="badgeStyle({ variant: props.variant, size: props.size })" v-bind="rootAttrs">
    <span v-if="iconLeadingForRender" data-badge-icon aria-hidden="true">
      <component
        :is="iconLeadingForRender"
        :size="ICON_SIZE"
        aria-hidden="true"
      />
    </span>
    <span data-badge-label>
      <slot />
    </span>
    <span v-if="slots['icon-trailing']" data-badge-icon aria-hidden="true">
      <slot name="icon-trailing" />
    </span>
  </span>
</template>
